import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Save, Eye, ArrowLeft, Plus, Trash2, Settings, Edit,
  Type, Image as ImageIcon, Layout, List, Quote, 
  Video, Code, Grid3x3, Columns, AlignLeft, Monitor, Tablet, Smartphone
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getPageDefaultContent } from '@/utils/pageDefaultContent';
import BlockEditorSidebar from '@/components/BlockEditorSidebar';

const PageBuilderPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === 'new';

  const [page, setPage] = useState({
    page_title: '',
    page_slug: '',
    meta_description: '',
    status: 'draft',
    blocks: []
  });

  const [selectedBlock, setSelectedBlock] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewportMode, setViewportMode] = useState('desktop'); // 'desktop', 'tablet', 'mobile'
  const [loading, setLoading] = useState(!isNew);

  // Bibliothèque de blocs disponibles
  const blockLibrary = [
    { 
      type: 'hero', 
      icon: Layout, 
      name: 'Hero Section',
      defaultContent: {
        title: 'Titre Principal',
        subtitle: 'Sous-titre accrocheur',
        buttonText: 'En savoir plus',
        buttonLink: '#',
        backgroundImage: '',
        textAlign: 'center'
      }
    },
    { 
      type: 'text', 
      icon: Type, 
      name: 'Texte',
      defaultContent: {
        content: '<p>Contenu texte ici...</p>',
        fontSize: 'base',
        textAlign: 'left'
      }
    },
    { 
      type: 'heading', 
      icon: AlignLeft, 
      name: 'Titre',
      defaultContent: {
        text: 'Titre de section',
        level: 'h2',
        textAlign: 'left'
      }
    },
    { 
      type: 'image', 
      icon: ImageIcon, 
      name: 'Image',
      defaultContent: {
        url: 'https://via.placeholder.com/800x400',
        alt: 'Image',
        caption: '',
        width: '100%'
      }
    },
    { 
      type: 'columns', 
      icon: Columns, 
      name: 'Colonnes',
      defaultContent: {
        columns: [
          { content: '<p>Colonne 1</p>' },
          { content: '<p>Colonne 2</p>' }
        ],
        columnsCount: 2
      }
    },
    { 
      type: 'quote', 
      icon: Quote, 
      name: 'Citation',
      defaultContent: {
        text: 'Citation inspirante',
        author: 'Auteur',
        style: 'bordered'
      }
    },
    { 
      type: 'list', 
      icon: List, 
      name: 'Liste',
      defaultContent: {
        items: ['Élément 1', 'Élément 2', 'Élément 3'],
        type: 'bullet'
      }
    },
    { 
      type: 'video', 
      icon: Video, 
      name: 'Vidéo',
      defaultContent: {
        url: '',
        provider: 'youtube',
        width: '100%'
      }
    },
    { 
      type: 'spacer', 
      icon: Grid3x3, 
      name: 'Espacement',
      defaultContent: {
        height: '40px'
      }
    },
    { 
      type: 'html', 
      icon: Code, 
      name: 'HTML personnalisé',
      defaultContent: {
        html: '<div>Votre HTML ici</div>'
      }
    }
  ];

  // Utilise le contenu réel des pages statiques
  const getDefaultContent = (pageSlug) => {
    return getPageDefaultContent(pageSlug);
  };

  const getDefaultContentOLD = (pageSlug) => {
    const defaultPages = {
      'home': {
        page_title: 'Page d\'accueil',
        blocks: [
          {
            id: 'block_home_hero',
            type: 'hero',
            content: {
              title: 'Bienvenue sur SFOALC',
              subtitle: 'Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact',
              buttonText: 'En savoir plus',
              buttonLink: '/about',
              backgroundImage: '',
              textAlign: 'center'
            }
          },
          {
            id: 'block_home_text',
            type: 'text',
            content: {
              content: '<p>La SFOALC est une société savante regroupant les ophtalmologistes spécialisés dans l\'adaptation des lentilles de contact.</p>',
              fontSize: 'lg',
              textAlign: 'center'
            }
          }
        ]
      },
      'about': {
        page_title: 'À propos',
        blocks: [
          {
            id: 'block_about_heading',
            type: 'heading',
            content: { text: 'À propos de la SFOALC', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_about_text',
            type: 'text',
            content: {
              content: '<p>Présentation de la Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact.</p><p>Notre mission est de promouvoir l\'excellence dans l\'adaptation des lentilles de contact.</p>',
              fontSize: 'base',
              textAlign: 'left'
            }
          }
        ]
      },
      'contact': {
        page_title: 'Contact',
        blocks: [
          {
            id: 'block_contact_heading',
            type: 'heading',
            content: { text: 'Contactez-nous', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_contact_text',
            type: 'text',
            content: {
              content: '<p>Pour toute question, n\'hésitez pas à nous contacter.</p>',
              fontSize: 'base',
              textAlign: 'center'
            }
          }
        ]
      },
      'partners': {
        page_title: 'Partenaires',
        blocks: [
          {
            id: 'block_partners_heading',
            type: 'heading',
            content: { text: 'Nos Partenaires', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_partners_text',
            type: 'text',
            content: {
              content: '<p>Découvrez nos partenaires qui nous accompagnent dans notre mission.</p>',
              fontSize: 'base',
              textAlign: 'center'
            }
          }
        ]
      },
      'faq': {
        page_title: 'FAQ',
        blocks: [
          {
            id: 'block_faq_heading',
            type: 'heading',
            content: { text: 'Questions Fréquentes', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_faq_text',
            type: 'text',
            content: {
              content: '<p>Retrouvez les réponses aux questions les plus fréquemment posées.</p>',
              fontSize: 'base',
              textAlign: 'left'
            }
          }
        ]
      },
      'legal': {
        page_title: 'Mentions légales',
        blocks: [
          {
            id: 'block_legal_heading',
            type: 'heading',
            content: { text: 'Mentions Légales', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_legal_text',
            type: 'text',
            content: {
              content: '<p>Mentions légales du site SFOALC.</p>',
              fontSize: 'base',
              textAlign: 'left'
            }
          }
        ]
      },
      'privacy': {
        page_title: 'Politique de confidentialité',
        blocks: [
          {
            id: 'block_privacy_heading',
            type: 'heading',
            content: { text: 'Politique de Confidentialité', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_privacy_text',
            type: 'text',
            content: {
              content: '<p>Découvrez comment nous protégeons vos données personnelles.</p>',
              fontSize: 'base',
              textAlign: 'left'
            }
          }
        ]
      },
      'directory': {
        page_title: 'Annuaire',
        blocks: [
          {
            id: 'block_directory_heading',
            type: 'heading',
            content: { text: 'Annuaire des Praticiens', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_directory_text',
            type: 'text',
            content: {
              content: '<p>Trouvez un praticien spécialisé près de chez vous.</p>',
              fontSize: 'base',
              textAlign: 'center'
            }
          }
        ]
      },
      'adhesion': {
        page_title: 'Adhésion',
        blocks: [
          {
            id: 'block_adhesion_heading',
            type: 'heading',
            content: { text: 'Adhérer à la SFOALC', level: 'h1', textAlign: 'center' }
          },
          {
            id: 'block_adhesion_text',
            type: 'text',
            content: {
              content: '<p>Rejoignez notre communauté de professionnels.</p>',
              fontSize: 'base',
              textAlign: 'center'
            }
          }
        ]
      }
    };

    return defaultPages[pageSlug] || null;
  };

  const fetchPage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pages/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPage({
          page_title: data.page_title,
          page_slug: data.page_slug,
          meta_description: data.meta_description || '',
          status: data.status || 'draft',
          blocks: data.blocks || []
        });
      } else if (response.status === 404) {
        // Page not found in DB, load default content for static pages
        const defaultContent = getDefaultContent(slug);
        if (defaultContent) {
          setPage({
            page_title: defaultContent.page_title,
            page_slug: slug,
            meta_description: '',
            status: 'draft',
            blocks: defaultContent.blocks
          });
          toast.info('Contenu par défaut chargé - Vous pouvez maintenant le modifier');
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement de la page');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      fetchPage();
    }
  }, [slug]); // Only depend on slug, fetchPage is stable

  const handleSave = async () => {
    if (!page.page_title || !page.page_slug) {
      toast.error('Titre et slug obligatoires');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = isNew 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/pages`
        : `${process.env.REACT_APP_BACKEND_URL}/api/pages/${slug}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(page)
      });

      if (response.ok) {
        toast.success('Page sauvegardée avec succès !');
        if (isNew) {
          navigate(`/admin/page-builder/${page.page_slug}`);
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const addBlock = (blockType) => {
    const blockTemplate = blockLibrary.find(b => b.type === blockType);
    const newBlock = {
      id: `block_${Date.now()}`,
      type: blockType,
      content: { ...blockTemplate.defaultContent },
      style: {
        backgroundColor: '',
        backgroundImage: '',
        backgroundVideo: '',
        textColor: '',
        padding: { top: '16', right: '16', bottom: '16', left: '16' },
        margin: { top: '0', right: '0', bottom: '0', left: '0' },
        borderRadius: '0',
        borderWidth: '0',
        borderColor: '',
        borderStyle: 'solid',
        boxShadow: '',
        width: '100',
        maxWidth: '',
        minHeight: '',
        customCSS: '',
        // Typography
        fontFamily: '',
        fontSize: '',
        fontWeight: '',
        fontStyle: '',
        lineHeight: '',
        letterSpacing: '',
        wordSpacing: '',
        textTransform: '',
        textDecoration: ''
      }
    };

    setPage({ ...page, blocks: [...page.blocks, newBlock] });
    toast.success(`Bloc "${blockTemplate.name}" ajouté`);
  };

  const deleteBlock = (blockId) => {
    setPage({
      ...page,
      blocks: page.blocks.filter(b => b.id !== blockId)
    });
    toast.success('Bloc supprimé');
  };

  const updateBlockContent = (blockId, newContent) => {
    setPage({
      ...page,
      blocks: page.blocks.map(b => 
        b.id === blockId ? { ...b, content: { ...b.content, ...newContent } } : b
      )
    });
  };

  const updateBlockStyle = (blockId, newStyle) => {
    setPage({
      ...page,
      blocks: page.blocks.map(b => 
        b.id === blockId ? { ...b, style: { ...b.style, ...newStyle } } : b
      )
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(page.blocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setPage({ ...page, blocks: items });
  };

  const openBlockSettings = (block) => {
    setSelectedBlock(block);
    setSidebarOpen(true);
  };

  const getCanvasWidth = () => {
    switch (viewportMode) {
      case 'desktop': return '100%';
      case 'tablet': return '768px';
      case 'mobile': return '375px';
      default: return '100%';
    }
  };

  const getBlockStyles = (block) => {
    if (!block.style) return {};
    
    const style = block.style;
    const styles = {};

    // Background
    if (style.backgroundColor) styles.backgroundColor = style.backgroundColor;
    if (style.backgroundImage) {
      styles.backgroundImage = `url(${style.backgroundImage})`;
      styles.backgroundSize = 'cover';
      styles.backgroundPosition = 'center';
    }
    if (style.textColor) styles.color = style.textColor;

    // Spacing
    if (style.padding) {
      styles.padding = `${style.padding.top}px ${style.padding.right}px ${style.padding.bottom}px ${style.padding.left}px`;
    }
    if (style.margin) {
      styles.margin = `${style.margin.top}px ${style.margin.right}px ${style.margin.bottom}px ${style.margin.left}px`;
    }

    // Border
    if (style.borderRadius) styles.borderRadius = `${style.borderRadius}px`;
    if (style.borderWidth && parseInt(style.borderWidth) > 0) {
      styles.border = `${style.borderWidth}px ${style.borderStyle || 'solid'} ${style.borderColor || '#000'}`;
    }

    // Shadow
    if (style.boxShadow) styles.boxShadow = style.boxShadow;

    // Dimensions
    if (style.width && style.width !== '100') styles.width = `${style.width}%`;
    if (style.maxWidth) styles.maxWidth = `${style.maxWidth}px`;
    if (style.minHeight) styles.minHeight = `${style.minHeight}px`;

    // Typography
    if (style.fontFamily) styles.fontFamily = style.fontFamily;
    if (style.fontSize) styles.fontSize = `${style.fontSize}px`;
    if (style.fontWeight) styles.fontWeight = style.fontWeight;
    if (style.fontStyle) styles.fontStyle = style.fontStyle;
    if (style.lineHeight) styles.lineHeight = style.lineHeight;
    if (style.letterSpacing) styles.letterSpacing = `${style.letterSpacing}px`;
    if (style.wordSpacing) styles.wordSpacing = `${style.wordSpacing}px`;
    if (style.textTransform) styles.textTransform = style.textTransform;
    if (style.textDecoration) styles.textDecoration = style.textDecoration;

    return styles;
  };

  const renderBlock = (block, isDragging = false) => {
    const { type, content } = block;
    const blockStyles = getBlockStyles(block);

    const containerStyle = { ...blockStyles, position: 'relative' };

    // Video background
    let videoBackground = null;
    if (block.style?.backgroundVideo) {
      const videoId = block.style.backgroundVideo.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      if (videoId) {
        videoBackground = (
          <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&modestbranding=1`}
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ minWidth: '100%', minHeight: '100%', transform: 'scale(1.5)' }}
              allow="autoplay; encrypted-media"
            />
          </div>
        );
      }
    }

    // Overlay for image/video backgrounds
    let overlay = null;
    if ((block.style?.backgroundImage || block.style?.backgroundVideo) && block.style?.overlayColor) {
      overlay = (
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundColor: block.style.overlayColor,
            zIndex: 0
          }}
        />
      );
    }

    switch (type) {
      case 'hero':
        return (
          <div style={containerStyle} className={`relative rounded-xl text-${content.textAlign} ${!block.style?.backgroundColor && !block.style?.backgroundImage ? 'bg-[#1e3a5f] text-white' : ''}`}>
            {videoBackground}
            {overlay}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h1 className="text-4xl font-bold mb-4">{content.title}</h1>
              <p className="text-xl mb-6 opacity-90">{content.subtitle}</p>
              {content.buttonText && (
                <button className="bg-white text-[#1e3a5f] px-6 py-3 rounded-lg font-bold hover:bg-gray-100">
                  {content.buttonText}
                </button>
              )}
            </div>
          </div>
        );

      case 'heading':
        const HeadingTag = content.level || 'h2';
        return (
          <HeadingTag className={`font-bold text-${content.textAlign} ${content.level === 'h1' ? 'text-4xl' : content.level === 'h2' ? 'text-3xl' : 'text-2xl'}`}>
            {content.text}
          </HeadingTag>
        );

      case 'text':
        return (
          <div 
            className={`text-${content.fontSize} text-${content.textAlign}`}
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        );

      case 'image':
        return (
          <div className="text-center">
            <img src={content.url} alt={content.alt} className="mx-auto rounded-lg" style={{ width: content.width }} />
            {content.caption && <p className="text-sm text-gray-600 mt-2">{content.caption}</p>}
          </div>
        );

      case 'columns':
        return (
          <div className={`grid grid-cols-${content.columnsCount} gap-6`}>
            {content.columns.map((col, idx) => (
              <div key={idx} dangerouslySetInnerHTML={{ __html: col.content }} />
            ))}
          </div>
        );

      case 'quote':
        return (
          <blockquote className={`${content.style === 'bordered' ? 'border-l-4 border-cyan-500 pl-4' : 'text-center'} italic text-xl`}>
            <p>&ldquo;{content.text}&rdquo;</p>
            {content.author && <footer className="text-gray-600 mt-2">— {content.author}</footer>}
          </blockquote>
        );

      case 'list':
        const ListTag = content.type === 'numbered' ? 'ol' : 'ul';
        return (
          <ListTag className={content.type === 'numbered' ? 'list-decimal list-inside' : 'list-disc list-inside'}>
            {content.items.map((item, idx) => (
              <li key={idx} className="mb-2">{item}</li>
            ))}
          </ListTag>
        );

      case 'video':
        return (
          <div className="aspect-video">
            <iframe
              src={content.url}
              className="w-full h-full rounded-lg"
              allowFullScreen
            />
          </div>
        );

      case 'spacer':
        return <div style={{ height: content.height }} />;

      case 'html':
        return <div dangerouslySetInnerHTML={{ __html: content.html }} />;

      default:
        return <div className="p-4 bg-gray-100 rounded">Bloc {type}</div>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/pages')}>
              <ArrowLeft size={16} className="mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-lg font-bold">{isNew ? 'Nouvelle Page' : page.page_title}</h1>
              <p className="text-xs text-gray-600">/{page.page_slug || 'nouveau-slug'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Responsive Toolbar */}
            <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1 bg-gray-50">
              <button
                onClick={() => setViewportMode('desktop')}
                className={`p-2 rounded transition-all ${
                  viewportMode === 'desktop' 
                    ? 'bg-cyan-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                title="Vue Desktop"
              >
                <Monitor size={18} />
              </button>
              <button
                onClick={() => setViewportMode('tablet')}
                className={`p-2 rounded transition-all ${
                  viewportMode === 'tablet' 
                    ? 'bg-cyan-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                title="Vue Tablet"
              >
                <Tablet size={18} />
              </button>
              <button
                onClick={() => setViewportMode('mobile')}
                className={`p-2 rounded transition-all ${
                  viewportMode === 'mobile' 
                    ? 'bg-cyan-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                title="Vue Mobile"
              >
                <Smartphone size={18} />
              </button>
            </div>

            <Button size="sm" onClick={handleSave} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
              <Save size={16} className="mr-2" />
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Main Content - Sidebar Left + Canvas Right */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Block Editor (Élémentor Style) */}
          {sidebarOpen && selectedBlock && (
            <BlockEditorSidebar
              open={sidebarOpen}
              block={selectedBlock}
              blockLibrary={blockLibrary}
              onClose={() => {
                setSidebarOpen(false);
                setSelectedBlock(null);
              }}
              updateBlockContent={updateBlockContent}
              updateBlockStyle={updateBlockStyle}
            />
          )}

          {/* Left Sidebar - Block Library (Default) */}
          {!sidebarOpen && (
            <div className="w-72 bg-gray-50 border-r border-gray-200 overflow-y-auto p-4 shrink-0">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-cyan-700">
                <Plus size={20} />
                Bibliothèque de Blocs
              </h3>

              <div className="space-y-2 mb-6">
                {blockLibrary.map((block) => (
                  <button
                    key={block.type}
                    onClick={() => addBlock(block.type)}
                    className="w-full flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-all hover:shadow-md"
                  >
                    <block.icon size={20} className="text-[#1e3a5f]" />
                    <span className="text-sm font-medium">{block.name}</span>
                  </button>
                ))}
              </div>

              <div className="pt-6 border-t border-gray-300">
                <h3 className="font-bold text-sm mb-3 text-gray-800">⚙️ Paramètres de la Page</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Titre</Label>
                    <Input
                      value={page.page_title}
                      onChange={(e) => setPage({ ...page, page_title: e.target.value })}
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Slug</Label>
                    <Input
                      value={page.page_slug}
                      onChange={(e) => setPage({ ...page, page_slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Description</Label>
                    <textarea
                      value={page.meta_description}
                      onChange={(e) => setPage({ ...page, meta_description: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Canvas - Page Preview */}
          <div className="flex-1 overflow-y-auto bg-gray-100 p-6 flex justify-center">
            <div 
              className="bg-white rounded-lg shadow-2xl transition-all duration-300 mx-auto"
              style={{ 
                width: getCanvasWidth(),
                minHeight: '100%'
              }}
            >
              <div className="p-8">
                {page.blocks.length === 0 ? (
                  <div className="text-center py-16">
                    <Layout className="mx-auto text-gray-400 mb-4" size={64} />
                    <h3 className="text-xl font-bold text-gray-600 mb-2">Votre page est vide</h3>
                    <p className="text-gray-500">Ajoutez des blocs depuis la bibliothèque à gauche</p>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="blocks">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-4"
                        >
                          {page.blocks.map((block, index) => (
                            <Draggable
                              key={block.id}
                              draggableId={block.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`relative group ${snapshot.isDragging ? 'opacity-50' : ''}`}
                                >
                                  {/* Drag Handle */}
                                  <div
                                    {...provided.dragHandleProps}
                                    className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move"
                                  >
                                    <div className="bg-cyan-500 text-white border-2 border-cyan-600 rounded-lg p-2 shadow-sm">
                                      <Grid3x3 size={16} />
                                    </div>
                                  </div>

                                  <div 
                                    className="border-2 border-transparent group-hover:border-cyan-400 cursor-pointer rounded-lg p-4 transition-all relative bg-white"
                                    onClick={(e) => {
                                      // Évite que le clic sur des éléments internes ne déclenche l'ouverture
                                      if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.closest('button')) {
                                        return;
                                      }
                                      openBlockSettings(block);
                                    }}
                                  >
                                    <div onClick={(e) => e.stopPropagation()}>
                                      {renderBlock(block, snapshot.isDragging)}
                                    </div>
                                    
                                    {/* Overlay d'édition au survol */}
                                    <div 
                                      className="absolute inset-0 bg-cyan-500 bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openBlockSettings(block);
                                      }}
                                      style={{ pointerEvents: 'none' }}
                                    >
                                      <div 
                                        className="opacity-0 group-hover:opacity-100 bg-white px-4 py-2 rounded-lg shadow-xl flex items-center gap-2 text-sm font-bold text-cyan-700 border-2 border-cyan-400 cursor-pointer"
                                        style={{ pointerEvents: 'auto' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openBlockSettings(block);
                                        }}
                                      >
                                        <Edit size={16} />
                                        Cliquer pour modifier
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="absolute -right-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex flex-col gap-2 bg-white border-2 border-gray-300 rounded-lg p-1 shadow-sm">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openBlockSettings(block);
                                        }}
                                        className="p-2 hover:bg-blue-50 rounded transition-all"
                                        title="Modifier"
                                      >
                                        <Settings size={18} className="text-blue-600" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteBlock(block.id);
                                        }}
                                        className="p-2 hover:bg-red-50 rounded transition-all"
                                        title="Supprimer"
                                      >
                                        <Trash2 size={18} className="text-red-600" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PageBuilderPage;
